import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import FlashcardCard from '../components/FlashcardCard';
import { getFlashcards } from '../data/flashcardsProvider';

const NewJerseyRealEstateExam: React.FC = () => {
  const [showAnswer, setShowAnswer] = useState(false);
  const flashcards = getFlashcards('nj');
  const sampleCard = flashcards[0];

  const handleCardClick = () => {
    setShowAnswer(!showAnswer);
  };

  return (
    <>
      <Helmet>
        <title>New Jersey Real Estate Exam Preparation | RealEstart.io</title>
        <meta 
          name="description" 
          content="Prepare for your New Jersey real estate exam with 500 flashcards. Cover NJ license law, agency, brokerage, and national real estate principles." 
        />
        <meta name="keywords" content="New Jersey real estate exam, NJ real estate license, real estate flashcards, practice test" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://realestart.io/new-jersey-real-estate-exam" />
        <meta property="og:title" content="New Jersey Real Estate Exam Preparation | RealEstart.io" />
        <meta property="og:description" content="Prepare for your New Jersey real estate exam with 500 flashcards. Cover NJ license law, agency, brokerage, and national principles." />
        
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://realestart.io/new-jersey-real-estate-exam" />
        <meta property="twitter:title" content="New Jersey Real Estate Exam Preparation | RealEstart.io" />
        <meta property="twitter:description" content="Prepare for your New Jersey real estate exam with 500 flashcards." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOccupationalCredential",
            "name": "New Jersey Real Estate License",
            "description": "New Jersey real estate salesperson license preparation",
            "provider": { "@type": "Organization", "name": "RealEstart.io", "url": "https://realestart.io" },
            "educationalLevel": "Professional",
            "competencyRequired": "New Jersey Real Estate Commission",
            "credentialCategory": "License"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">R</span>
                </div>
                <span className="text-xl font-bold text-gray-900">RealEstart.io</span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link to="/auth" className="text-gray-600 hover:text-emerald-600 transition-colors">Sign In</Link>
                <Link to="/checkout?plan=trial" className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-2 rounded-full hover:from-emerald-700 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg">
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </header>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-6">
                New Jersey Real Estate Exam
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Ace Your New Jersey Real Estate Exam
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Master NJ license law, agency, brokerage, and national real estate principles with our study platform. 
                Practice with 500 flashcards and 75-question practice tests drawn from a 450-question bank.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/checkout?plan=trial&state=NJ" 
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Start Free Trial
                </Link>
                <Link 
                  to="/demo" 
                  className="border-2 border-emerald-600 text-emerald-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-emerald-600 hover:text-white transition-all"
                >
                  Try Demo
                </Link>
              </div>
            </div>

            <div className="max-w-md mx-auto mb-16">
              <h3 className="text-2xl font-bold text-center mb-6 text-gray-900">Try a Sample Question</h3>
              <FlashcardCard
                card={sampleCard}
                onCardClick={handleCardClick}
                onSwipe={() => {}}
                showAnswer={showAnswer}
                isAnimating={false}
              />
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">New Jersey Real Estate Exam Structure</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The New Jersey real estate salesperson exam covers national principles and NJ-specific law. 
                Our 500 flashcards and 450-question practice bank (75 questions per test) align with exam content.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">National Content</h3>
                <p className="text-blue-800 mb-4">
                  Agency, contracts, encumbrances, fair housing, financing, land use, leases, property ownership, transfer of title, and valuation &amp; math.
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-emerald-900 mb-6">New Jersey State Content</h3>
                <p className="text-emerald-800 mb-4">
                  NJ License Law and Professional Practice, Agency and Fair Housing, Brokerage Operations and Transactions, Property/Ownership/Land Use, and Finance/Valuation/Math.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Practice Test Format</h2>
              <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
                Each practice test randomly selects 75 questions from our 450-question bank—great for repeated practice.
              </p>
            </div>
            <div className="max-w-md mx-auto">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">75 Questions Per Test</h3>
                <div className="text-4xl font-bold mb-2">450-Question Bank</div>
                <p className="text-emerald-100">New set each time you start a test</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose RealEstart for New Jersey?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                NJ-specific categories plus national principles, with progress tracking.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">📚</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">500 NJ Flashcards</h3>
                <p className="text-gray-600">
                  NJ license law, agency, brokerage, property, and finance plus national principles.
                </p>
              </div>
              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">🎯</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">75 Questions Per Practice Test</h3>
                <p className="text-gray-600">
                  450-question bank: each test randomly selects 75 questions.
                </p>
              </div>
              <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl text-white">📊</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Progress Tracking</h3>
                <p className="text-gray-600">
                  Monitor your performance and focus on weak areas.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6">Ready to Pass Your New Jersey Real Estate Exam?</h2>
            <p className="text-xl mb-8 text-white/90">
              Start with 500 flashcards and practice tests drawn from a 450-question bank.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/checkout?plan=trial&state=NJ" 
                className="bg-white text-emerald-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Start Your Free Trial
              </Link>
              <Link 
                to="/demo" 
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-emerald-600 transition-all"
              >
                Try Demo First
              </Link>
            </div>
            <p className="mt-4 text-sm text-white/70">3-day free trial • No credit card required</p>
          </div>
        </section>

        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="text-2xl font-bold mb-4">RealEstart.io</div>
              <p className="text-gray-400 mb-4">
                Your trusted companion for New Jersey real estate education and licensing preparation.
              </p>
              <p className="text-sm text-gray-500">
                &copy; 2024 RealEstart.io. This is a study aid and is not affiliated with the NJ Real Estate Commission or any official exam provider.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default NewJerseyRealEstateExam;
